import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Form } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from '@/components/ui/use-toast'

import { Button } from '@/components/ui/button'
import { MyFormInput, MyFromTextarea } from 'components/ui/MyFormField'
import { PainterPicker } from 'components/PainterPicker'
import useSWR, { mutate } from 'swr'

import Loader from '@/components/ui/Loader'
import { putFetcher } from 'api/swr'
import { displayNameFromFields } from 'commons/userDisplayName'
import useSWRMutation from 'swr/mutation'
import TemplateCover from './Cover'
import TemplatePreview from './Preview'
import { TemplateInitDataInfoDisplay } from 'AdminPanel/Spine/editor/panel/info-panel/components/TemplateInitDataInfoDisplay'
import { getFullscreenTypeLabel } from 'Editor/utils/fullscreenType'

const infoFormSchema = z.object({
  coverField: z.enum(['cover', 'sketch'], {
    required_error: 'Please select a cover.',
  }),
  cover: z.string().optional(),
  sketch: z.string().optional(),
  initDataId: z.string().optional(),
  name: z
    .string()
    .min(2, {
      message: 'Username must be at least 2 characters.',
    })
    .max(30, {
      message: 'Username must not be longer than 30 characters.',
    }),
  intro: z.string().max(640).min(4),
  onlinePublisher: z.string(),
  preview: z.string().optional(),
  previewA: z.string().optional(),
  previewId: z.string().optional(),
  videoCover: z.string().optional(),
  videoCoverPreview: z.string().optional(),
  videoPreview: z.string().optional(),
  videoPreviewPreview: z.string().optional(),
})

const MediaPreviewDisplay = ({ form, template }) => {
  const coverField = form.watch('coverField', template?.coverField ?? 'cover')
  const cover =
    coverField === 'sketch'
      ? form.watch('sketch', template?.sketch)
      : form.watch('cover', template?.cover)
  const videoCover =
    coverField === 'sketch' ? '' : form.watch('videoCover', template?.videoCover)
  const videoCoverPreview =
    coverField === 'sketch'
      ? ''
      : form.watch('videoCoverPreview', template?.videoCoverPreview)
  const preview = form.watch('preview', template?.preview)
  const videoPreview = form.watch('videoPreview', template?.videoPreview)
  const videoPreviewPreview = form.watch(
    'videoPreviewPreview',
    template?.videoPreviewPreview,
  )

  const isFullscreen = template?.fullscreenType === 2
  const isPortrait = template?.fullscreenType === 3
  return (
    <div className="rounded-md border p-3">
      <TemplateInitDataInfoDisplay
        cover={cover}
        videoCover={videoCover}
        videoCoverPreview={videoCoverPreview}
        preview={preview}
        videoPreview={videoPreview}
        videoPreviewPreview={videoPreviewPreview}
        tileClassName={
          isFullscreen
            ? 'h-40 w-[90px] shrink-0'
            : isPortrait
              ? 'h-40 w-[120px] shrink-0'
              : undefined
        }
      />
    </div>
  )
}

const InfoPanel = () => {
  const { tid } = useParams()
  const { data: template } = useSWR([`/templates/${tid}`])
  const [defaultValues] = useState({
    coverField: template?.coverField ?? 'cover',
    sketch: template?.sketch,
    cover: template?.cover,
    initDataId: template?.initDataId,
    name: template?.name ?? '',
    intro: template?.intro ?? '',
    preview: template?.preview,
    previewA: template?.previewA,
    previewId: template?.previewId ?? '',
    videoCover: template?.videoCover ?? '',
    videoCoverPreview: template?.videoCoverPreview ?? '',
    videoPreview: template?.videoPreview ?? '',
    videoPreviewPreview: template?.videoPreviewPreview ?? '',
    onlinePublisher: template?.author,
  })

  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues,
  })
  const authorId = form.watch('onlinePublisher')
  const { data: selectedPainter } = useSWR(
    authorId ? [`/publishers/${authorId}`] : null,
  )

  const { trigger, isMutating, error } = useSWRMutation(
    `/templates/${template.id}`,
    putFetcher,
    {
      onSuccess: () => {
        mutate(['/templates', { tid }])
        toast({
          title: '更新成功',
          description: '模版信息已保存。',
          className: 'bg-green-500 text-white',
        })
      },
      onError: (err) => {
        toast({
          title: '更新失败',
          description: err?.message || '请稍后重试',
          variant: 'destructive',
        })
      },
    },
  )

  const onSubmit = (data) => {
    if (isMutating) return
    const user = selectedPainter
    if (!user) {
      toast({
        title: 'Painter not found',
        description: 'Please select a painter',
        variant: 'destructive',
      })
      return
    }
    data.author = user.id
    data.onlinePublisher = displayNameFromFields(user)
    // 手绘封面（sketch）为静态图，不携带 cover 视频
    if (data.coverField !== 'cover') {
      data.videoCover = ''
      data.videoCoverPreview = ''
    }
    trigger({ key: 'form', value: data })
  }

  if (!template) return null
  return (
    <div className="h-full min-h-0 overflow-y-auto rounded-md border p-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <TemplateCover form={form} template={template} />
            </div>
            <div className="flex-1">
              <TemplatePreview form={form} template={template} />
            </div>
          </div>
          <MediaPreviewDisplay form={form} template={template} />
          <MyFormInput
            name="name"
            form={form}
            label="Name"
            placeholder={'template name'}
          />
          <div className="space-y-1">
            <div className="text-sm font-medium">画幅比例</div>
            <div className="text-sm text-muted-foreground">
              {getFullscreenTypeLabel(template.fullscreenType)}
              （创建后不可修改）
            </div>
          </div>
          <MyFromTextarea
            name="intro"
            form={form}
            label="Description"
            placeholder={'template description'}
            rows={4}
          />
          <PainterPicker
            className="w-1/2"
            label="Painter"
            placeholder="template Painter"
            value={authorId}
            onSelect={(userId) => {
              form.setValue('onlinePublisher', userId, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />
          {isMutating ? (
            <Loader>Updating...</Loader>
          ) : (
            <Button type="submit">Update</Button>
          )}
          {error && <div className="text-red-500">{error.message}</div>}
        </form>
      </Form>
    </div>
  )
}

export default InfoPanel
